// ActionTable: displays action recommendations with focus area, priority, and description.

import React from 'react';
import { t, focusLabel } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import type { Action } from '../lib/api';

interface ActionTableProps {
  actions: Action[];
  lang: Lang;
}

function FocusBadge({ focus, lang }: { focus: Action['focus']; lang: Lang }) {
  const classes: Record<Action['focus'], string> = {
    PRODUCT: 'badge-product',
    SUPPORT: 'badge-support',
    GROWTH: 'badge-growth',
    MARKET_WATCH: 'badge-market_watch',
  };
  return <span className={classes[focus]}>{focusLabel(focus, lang)}</span>;
}

function PriorityBadge({ priority }: { priority: Action['priority'] }) {
  const classes: Record<Action['priority'], string> = {
    P1: 'badge-p1',
    P2: 'badge-p2',
    P3: 'badge-p3',
  };
  return <span className={classes[priority]}>{priority}</span>;
}

export function ActionTable({ actions, lang }: ActionTableProps) {
  if (actions.length === 0) {
    return (
      <div className="card p-6 text-neutral-500 text-sm text-center">
        {t('noData', lang)}
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <table className="data-table">
        <thead>
          <tr>
            <th className="w-12">{t('priority', lang) ?? 'P'}</th>
            <th className="w-32">{lang === 'tr' ? 'Odak' : 'Focus'}</th>
            <th>{t('actionRecommendations', lang)}</th>
          </tr>
        </thead>
        <tbody>
          {actions.map((action) => (
            <tr key={action.id}>
              <td className="align-top pt-3">
                <PriorityBadge priority={action.priority} />
              </td>
              <td className="align-top pt-3">
                <FocusBadge focus={action.focus} lang={lang} />
              </td>
              <td>
                <div className="font-medium text-neutral-800 text-sm mb-0.5">{action.title}</div>
                <div className="text-xs text-neutral-500 leading-relaxed">{action.description}</div>
                {action.relatedTheme && (
                  <div className="text-xs text-neutral-500 mt-1 italic">{action.relatedTheme}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
